import { useEffect, useState, useCallback } from "react";
import { Button, Form } from "antd";
import { Body } from "../../../components/main-body/body";
import editIcon from "../../../assets/edit.svg";
import { useTranslation } from "react-i18next";
import useNotification from "../../../components/notification/notification";
import "./life-bot.css";
import ApiLifeBotManagement from "../../../api/lifebot";
import ApiChatBotManagement from "../../../api/chatbot";
import { FormInput, FormSwitch } from "../../../components/form/form";
import classNames from "classnames";
import { Editor } from "../../../components/editor/editor";
import Markdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import useAdminEvents from "../../../hooks/useAdminEvents";

const LifeAndChatSettings = () => {
  const { t } = useTranslation();
  const { openNotification } = useNotification();

  // --- LIFE (table 1) ---
  const [isEditLife, setIsEditLife] = useState(false);
  const [hasChangesLife, setHasChangesLife] = useState(false);
  const [lifeSettings, setLifeSettings] = useState([]);
  const [initialLife, setInitialLife] = useState([]);
  const [lifeForm] = Form.useForm();

  const toggleEditLife = () => { setIsEditLife(v => !v); setHasChangesLife(false); };
  const cancelLife = () => { setIsEditLife(false); setHasChangesLife(false); setLifeSettings(initialLife); };
  const updateLifeSettings = async () => {
    await ApiLifeBotManagement.update(lifeSettings);
    openNotification({ type: "success", message: <p>{t("lifebot.notification.success")}</p> });
    setInitialLife(lifeSettings);
    toggleEditLife();
  };
  const onLifeChange = useCallback((key, v) => {
    if (!hasChangesLife) setHasChangesLife(true);
    const value = +v.target.value;
    setLifeSettings(prev => prev.map(it => it.key === key ? { ...it, value } : it));
  }, [hasChangesLife]);

  // --- CHAT (table 2) ---
  const [isEditChat, setIsEditChat] = useState(false);
  const [hasChangesChat, setHasChangesChat] = useState(false);
  const [chatSettings, setChatSettings] = useState([]);
  const [initialChat, setInitialChat] = useState([]);
  const [chatForm] = Form.useForm();

  const loadSettings = useCallback(async () => {
    const [lifeRes, chatRes] = await Promise.all([
      ApiLifeBotManagement.get(),
      ApiChatBotManagement.get(),
    ]);
    const life = lifeRes?.data?.settings || [];
    const chat = chatRes?.data?.settings || [];
    setLifeSettings(life); setInitialLife(life);
    setChatSettings(chat); setInitialChat(chat);
  }, []);

  const toggleEditChat = () => { setIsEditChat(v => !v); setHasChangesChat(false); };
  const cancelChat = () => { setIsEditChat(false); setHasChangesChat(false); setChatSettings(initialChat); };
  const updateChatSettings = async () => {
    await ApiChatBotManagement.update(chatSettings);
    openNotification({ type: "success", message: <p>{t("chatbot.notification.success")}</p> });
    setInitialChat(chatSettings);
    toggleEditChat();
  };
  const onChatChange = useCallback((key, v) => {
    if (!hasChangesChat) setHasChangesChat(true);

    let value;
    if (typeof v === "boolean") value = v ? 1 : 0;                // Switch
    else if (v?.target?.value !== undefined) value = +v.target.value; // numeric
    else value = v;                                               // markdown string

    setChatSettings(prev => prev.map(it => it.key === key ? { ...it, value } : it));
  }, [hasChangesChat]);

  // --- LOAD ---
  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  useAdminEvents({
    onSettingChanged: (payload) => {
      if (payload?.group === "lifebot" || payload?.group === "chatbot") {
        loadSettings();
      }
    },
  });

  const isChatBoolean = (key) => key === "chatbotEnable";
  const isChatMarkdown = (key) => key === "chatbotContent";

  return (
    <Body header={{ title: t("lifebot.title") }}>
      {/* ===== Table 1: LifeBot settings ===== */}
      <div className="profile_form-container">
        <div className="profile_form__header" style={{ marginBottom: 12 }}>
          <span className="profile_form__header__title">{t("lifebot.settings")}</span>
          <div style={{ height: 40 }}>
            {isEditLife ? (
              <div className="profile-header__btns">
                <Button style={{ height: 40, background: "rgba(108,108,137,0.08)" }} onClick={cancelLife}>
                  {t("profile.btns.cancel")}
                </Button>
                <Button type="primary" style={{ marginLeft: 8, height: 40 }} onClick={updateLifeSettings} disabled={!hasChangesLife}>
                  {t("profile.btns.save")}
                </Button>
              </div>
            ) : (
              <img className="profile_form__header__edit-btn" src={editIcon} alt="edit" role="presentation" onClick={toggleEditLife}/>
            )}
          </div>
        </div>

        <div className="lifepoints-content-container" style={{ marginBottom: 32 }}>
          <div className="lifepoints-content-item">
            <div>
              {lifeSettings.map((item) => (
                <p key={item.key} className="lifepoints-content-item-activity">{item.name}</p>
              ))}
            </div>
          </div>
          <div className="lifepoints-content-item">
            <div>
              <Form form={lifeForm}>
                {lifeSettings.map((item) => (
                  <FormInput
                    key={item.key}
                    name={item.name}
                    readOnly={!isEditLife}
                    type="number"
                    className={classNames("lifepoints-content-item-points", {
                      "lifepoints-content-item-points--edit-mode": isEditLife,
                    })}
                    value={item.value}
                    bordered={false}
                    onChange={(v) => onLifeChange(item.key, v)}
                  />
                ))}
              </Form>
            </div>
          </div>
        </div>
      </div>
      {/* ===== Table 2: ChatBot settings ===== */}
      <div className="profile_form-container">
        <div className="profile_form__header" style={{ marginBottom: 12 }}>
          <span className="profile_form__header__title">{t("chatbot.title")}</span>
          <div style={{ height: 40 }}>
            {isEditChat ? (
              <div className="profile-header__btns">
                <Button style={{ height: 40, background: "rgba(108,108,137,0.08)" }} onClick={cancelChat}>
                  {t("profile.btns.cancel")}
                </Button>
                <Button type="primary" style={{ marginLeft: 8, height: 40 }} onClick={updateChatSettings} disabled={!hasChangesChat}>
                  {t("profile.btns.save")}
                </Button>
              </div>
            ) : (
              <img className="profile_form__header__edit-btn" src={editIcon} alt="edit" role="presentation" onClick={toggleEditChat}/>
            )}
          </div>
        </div>

        <div>
          <Form form={chatForm}>
            {chatSettings.map((item) => (
              <div className="chatbot-content-container" key={item.key} style={{ display: "flex", alignItems: "flex-start", gap: 24, marginBottom: 24 }}>
                <div className="chatbot-content-item" style={{ fontWeight: 500, lineHeight: "22px" }}>
                  <p className="chatbot-content-item-activity" style={{ marginBottom: 0 }}>{item.name}</p>
                </div>

                <div className="chatbot-content-item">
                  {isChatMarkdown(item.key) ? (
                    isEditChat ? (
                      <Editor
                        content={item.value || ""}
                        onChange={(value) => onChatChange(item.key, value)}
                        disableImageUpload={true}
                      />
                    ) : (
                      <Markdown className="markdown-editor lf-editor lf-editor__readonly" rehypePlugins={[rehypeRaw]}>
                        {item.value || ""}
                      </Markdown>
                    )
                  ) : isChatBoolean(item.key) ? (
                    <Form.Item name={item.key} style={{ marginTop: 0 }}>
                      <FormSwitch
                        checked={!!item.value}
                        onChange={(v) => onChatChange(item.key, v)}
                        readOnly={!isEditChat}
                        className={classNames("chatbot-content-item-points", {
                          "chatbot-content-item-points--edit-mode": isEditChat,
                        })}
                      />
                    </Form.Item>
                  ) : (
                    <FormInput
                      name={item.name}
                      readOnly={!isEditChat}
                      type="number"
                      className={classNames("chatbot-content-item-points", {
                        "chatbot-content-item-points--edit-mode": isEditChat,
                      })}
                      value={item.value}
                      bordered={false}
                      onChange={(v) => onChatChange(item.key, v)}
                    />
                  )}
                </div>
              </div>
            ))}
          </Form>
        </div>
      </div>
    </Body>
  );
};

export default LifeAndChatSettings;
